// Modelli per i messaggi Google Pub/Sub

export interface PubSubMessage {
  id: string;
  data: string; // Base64 encoded
  attributes: { [key: string]: string };
  publishTime: string;
  deliveryAttempt?: number;
  orderingKey?: string;
}

export interface PubSubMessageData {
  messageId: string;
  topic: string;
  subscription: string;
  data: any;
  attributes: { [key: string]: string };
  publishTime: Date;
  deliveryAttempt?: number;
  orderingKey?: string;
}

// Tipi di messaggi specifici per l'applicazione
export enum MessageType {
  FASCICOLO_CREATED = 'fascicolo.created',
  FASCICOLO_UPDATED = 'fascicolo.updated',
  FASCICOLO_DELETED = 'fascicolo.deleted',
  WIZARD_STARTED = 'wizard.started',
  WIZARD_COMPLETED = 'wizard.completed',
  WIZARD_FAILED = 'wizard.failed',
  FILE_UPLOADED = 'file.uploaded',
  FILE_PROCESSED = 'file.processed',
  NOTIFICATION = 'notification',
  SYSTEM_ALERT = 'system.alert',
  DOMANDA_SOSTEGNO_PUBBLICATA = 'domanda_sostegno_pubblicata'
}

export interface FascicoloMessage {
  type: MessageType.FASCICOLO_CREATED | MessageType.FASCICOLO_UPDATED | MessageType.FASCICOLO_DELETED;
  fascicoloId: string;
  beneficiario: {
    nome: string;
    cognome: string;
    codiceFiscale: string;
  };
  timestamp: Date;
  userId?: string;
  metadata?: { [key: string]: any };
}

export interface WizardMessage {
  type: MessageType.WIZARD_STARTED | MessageType.WIZARD_COMPLETED | MessageType.WIZARD_FAILED;
  wizardId: string;
  fascicoloId?: string;
  step: number;
  totalSteps: number;
  progress: number;
  timestamp: Date;
  userId?: string;
  error?: string;
  metadata?: { [key: string]: any };
}

export interface FileMessage {
  type: MessageType.FILE_UPLOADED | MessageType.FILE_PROCESSED;
  fileId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  fascicoloId?: string;
  wizardId?: string;
  timestamp: Date;
  userId?: string;
  processingResult?: any;
  metadata?: { [key: string]: any };
}

export interface NotificationMessage {
  type: MessageType.NOTIFICATION;
  notificationId: string;
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'info' | 'warning' | 'error' | 'success';
  userId?: string;
  timestamp: Date;
  expiresAt?: Date;
  metadata?: { [key: string]: any };
}

export interface SystemAlertMessage {
  type: MessageType.SYSTEM_ALERT;
  alertId: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  component: string;
  message: string;
  timestamp: Date;
  resolved: boolean;
  metadata?: { [key: string]: any };
}

// Union type per tutti i tipi di messaggi
export type ApplicationMessage =
  | FascicoloMessage
  | WizardMessage
  | FileMessage
  | NotificationMessage
  | SystemAlertMessage
  | DomandaSostegnoPubblicataAppMessage;

// Messaggio app per DOMANDA_SOSTEGNO_PUBBLICATA
export interface DomandaSostegnoPubblicataAppMessage {
  type: MessageType.DOMANDA_SOSTEGNO_PUBBLICATA;
  [key: string]: any;
}

// Configurazione per i topic
export interface TopicConfig {
  name: string;
  displayName: string;
  description: string;
  messageType: MessageType;
  retentionDuration?: string; // ISO 8601 duration
  enableMessageOrdering?: boolean;
  enableCompression?: boolean;
}

// Configurazione per le subscription
export interface SubscriptionConfig {
  name: string;
  topic: string;
  displayName: string;
  description: string;
  ackDeadlineSeconds?: number;
  messageRetentionDuration?: string; // ISO 8601 duration
  enableMessageOrdering?: boolean;
  filter?: string; // Pub/Sub filter expression
  deadLetterPolicy?: {
    deadLetterTopic: string;
    maxDeliveryAttempts: number;
  };
}

// Risultato dell'invio di un messaggio
export interface PublishResult {
  messageId: string;
  success: boolean;
  error?: string;
  timestamp: Date;
}

// Risultato della ricezione di un messaggi
export interface ReceiveResult {
  message: PubSubMessageData;
  ackId: string;
  success: boolean;
  error?: string;
  timestamp: Date;
}

// Statistiche per topic e subscription
export interface PubSubStats {
  topic: string;
  subscription?: string;
  messageCount: number;
  byteCount: number;
  oldestMessageAge?: number;
  newestMessageAge?: number;
  lastUpdated: Date;
}

// Configurazione di connessione
export interface PubSubConfig {
  projectId: string;
  keyFilename?: string;
  credentials?: {
    client_email: string;
    private_key: string;
  };
  apiEndpoint?: string;
  port?: number;
  sslCreds?: any;
  maxConnections?: number;
  maxIdleTime?: number;
  keepAliveInterval?: number;
}
